// src/pages/EmployeeLogin.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Avatar,
  Divider,
  Link,
  InputAdornment,
  IconButton,
  Fade,
  Zoom,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { 
  Fingerprint as FingerprintIcon,
  Person as PersonIcon,
  VpnKey as VpnKeyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Diamond as DiamondIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  LocalHospital as HospitalIcon,
  ArrowForward as ArrowForwardIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMutation } from '@apollo/client/react';
import { LOGIN_MUTATION } from '../graphql/auth.graphql';
import { LoginResponse, LoginVariables } from '../types/user.types';
import { styled } from '@mui/material/styles';
import { User } from '../types/user.types';

// Luxury color palette
const colors = {
  gold: '#C5A572',
  goldLight: '#E5D3B5',
  goldDark: '#8B7355',
  burgundy: '#800020',
  burgundyLight: '#A52A2A',
  navy: '#1A1F2E',
  navyLight: '#2A2F3F',
  cream: '#FDFAF3',
  creamDark: '#F5E6D3',
  charcoal: '#2C2C2C',
  emerald: '#046307',
  emeraldLight: '#2E7D32',
};

// Styled components
const LuxuryContainer = styled(Container)(() => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.burgundy} 50%, ${colors.navy} 100%)`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 50%, rgba(255,215,0,0.1) 0%, transparent 50%)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -100,
    right: -100,
    width: 300,
    height: 300,
    background: 'radial-gradient(circle, rgba(197,165,114,0.2) 0%, transparent 70%)',
    borderRadius: '50%',
  },
}));

const LuxuryPaper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  background: `linear-gradient(135deg, ${alpha(colors.cream, 0.95)} 0%, #FFFFFF 100%)`,
  backdropFilter: 'blur(20px)',
  borderRadius: '32px',
  boxShadow: `0 25px 50px -12px ${alpha(colors.gold, 0.5)}`,
  padding: theme.spacing(2),
  border: `2px solid ${colors.goldLight}`,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${colors.gold} 0%, ${colors.burgundy} 50%, ${colors.navy} 100%)`,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const LuxuryAvatar = styled(Avatar)(({ theme }) => ({
  background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.burgundy} 100%)`,
  boxShadow: `0 10px 20px -5px ${alpha(colors.gold, 0.4)}`,
  color: colors.cream,
  width: 80,
  height: 80,
  marginBottom: theme.spacing(0),
  border: `3px solid ${colors.cream}`,
}));

const LuxuryButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.5),
  fontSize: '1.1rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  background: `linear-gradient(135deg, ${colors.burgundy} 0%, ${colors.navy} 100%)`,
  color: colors.cream,
  transition: 'all 0.3s ease',
  '&:hover': {
    background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.burgundy} 100%)`,
    boxShadow: `0 10px 20px -5px ${alpha(colors.burgundy, 0.4)}`,
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    background: alpha(colors.charcoal, 0.3),
  },
}));

const LuxuryTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: alpha(colors.cream, 0.8),
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: colors.cream,
    },
    '&.Mui-focused': {
      backgroundColor: colors.cream,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.gold,
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: colors.gold,
  },
});

const LuxuryChip = styled(Chip)({
  backgroundColor: alpha(colors.gold, 0.1),
  border: `1px solid ${colors.gold}`,
  color: colors.navy,
  fontWeight: 500,
  '& .MuiChip-icon': {
    color: colors.gold,
  },
});

// Fixed FloatingElement without custom delay prop
const FloatingElement = styled(Box)({
  position: 'absolute',
  animation: 'float 6s ease-in-out infinite',
  '@keyframes float': {
    '0%, 100%': { transform: 'translateY(0px)' },
    '50%': { transform: 'translateY(-20px)' },
  },
});

export const EmployeeLogin: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Check if already authenticated and redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const [loginMutation] = useMutation<LoginResponse, LoginVariables>(LOGIN_MUTATION);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data } = await loginMutation({
        variables: {
          input: {
            username,
            password
          }
        }
      });

      if (data?.login) {
        const { token, user } = data.login;
        // Map the user object to match your User interface
        const mappedUser: User = {
          UserId: user.id || 0,
          UserName: user.name || user.username || '',
          Loginname: user.username || '',
          Enabled: true,
          role: user.role,
          BranchId: user.branchId,
        };
        
        login(token, mappedUser);
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        }
        navigate('/dashboard');
      } else {
        setError('Invalid response from server');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      
      if (err.networkError) {
        setError('Network error. Please check your connection.');
      } else if (err.graphQLErrors && err.graphQLErrors.length > 0) {
        setError(err.graphQLErrors[0].message || 'Login failed');
      } else {
        setError(err.message || 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  return (
    <LuxuryContainer maxWidth={false}>
      {/* Decorative elements - removed delay prop */}
      <FloatingElement sx={{ top: '10%', left: '5%' }}>
        <DiamondIcon sx={{ fontSize: 60, color: alpha(colors.gold, 0.2) }} />
      </FloatingElement>
      <FloatingElement sx={{ bottom: '15%', right: '8%' }}>
        <HospitalIcon sx={{ fontSize: 80, color: alpha(colors.burgundy, 0.15) }} />
      </FloatingElement>
      <FloatingElement sx={{ top: '20%', right: '15%' }}>
        <SecurityIcon sx={{ fontSize: 50, color: alpha(colors.gold, 0.15) }} />
      </FloatingElement>

      <Zoom in={true} style={{ transitionDelay: '200ms' }}>
        <Box sx={{ width: '100%', maxWidth: 450, position: 'relative', zIndex: 1 }}>
          <LuxuryPaper elevation={0}>
            {/* Header */}
            <Box display="flex" flexDirection="column" alignItems="center">
              <LuxuryAvatar>
                <FingerprintIcon sx={{ fontSize: 40 }} />
              </LuxuryAvatar>
              
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.burgundy} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0,
                }}
              >
                Employee Login
              </Typography>
              
              <Typography variant="body2" sx={{ color: alpha(colors.charcoal, 0.7), mb: 0.5 }}>
                Secure access to lab management system
              </Typography>

              <Box display="flex" gap={1} mb={0.5}>
                <LuxuryChip 
                  icon={<SecurityIcon />} 
                  label="HIPAA Compliant" 
                  size="small"
                />
                <LuxuryChip 
                  icon={<SpeedIcon />} 
                  label="Enterprise Grade" 
                  size="small"
                />
              </Box>
            </Box>

            {/* Error Alert */}
            <Fade in={!!error}>
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 0.5, 
                  borderRadius: '12px',
                  border: `1px solid ${colors.burgundy}`,
                }}
              >
                {error}
              </Alert>
            </Fade>

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit}>
              <LuxuryTextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: colors.gold }} />
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? 'small' : 'medium'}
              />
              
              <LuxuryTextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKeyIcon sx={{ color: colors.gold }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                        sx={{ color: colors.gold }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? 'small' : 'medium'}
              />

              {/* Remember Me and Forgot Password */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <Box display="flex" alignItems="center">
                  <input
                    type="checkbox"
                    id="remember"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    style={{
                      width: 18,
                      height: 18,
                      marginRight: 8,
                      accentColor: colors.gold,
                    }}
                  />
                  <label htmlFor="remember" style={{ color: colors.navy, fontSize: '0.9rem' }}>
                    Remember me
                  </label>
                </Box>
                <Link 
                  component="button" 
                  variant="body2" 
                  sx={{ 
                    color: colors.burgundy,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Forgot password?
                </Link>
              </Box>

              <LuxuryButton
                type="submit"
                fullWidth
                sx={{ mt: 1, mb: 1 }}
                disabled={loading}
                endIcon={!loading && <ArrowForwardIcon />}
              >
                {loading ? 'Authenticating...' : 'Sign In'}
              </LuxuryButton>
              
              <Divider sx={{ my: 1, borderColor: alpha(colors.gold, 0.3) }}>
                <Chip 
                  label="OR" 
                  size="small" 
                  sx={{ 
                    bgcolor: alpha(colors.gold, 0.1),
                    color: colors.navy,
                    border: `1px solid ${colors.gold}`,
                  }} 
                />
              </Divider>
              
              <Box textAlign="center">
                <Typography variant="body2" sx={{ color: alpha(colors.charcoal, 0.7), mb: 1 }}>
                  Are you a patient?
                </Typography>
                <Button
                  component={RouterLink}
                  to="/self-registration"
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderColor: colors.gold,
                    color: colors.navy,
                    borderRadius: '12px',
                    py: 1.5,
                    '&:hover': {
                      borderColor: colors.burgundy,
                      backgroundColor: alpha(colors.gold, 0.1),
                    },
                  }}
                  endIcon={<InfoIcon />}
                >
                  Self Registration Portal
                </Button>
              </Box>

              {/* Security Info */}
              <Box mt={0} p={1} sx={{ bgcolor: alpha(colors.gold, 0.05), borderRadius: '12px' }}>
                <Typography variant="caption" sx={{ color: alpha(colors.charcoal, 0.6), display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SecurityIcon sx={{ fontSize: 14, color: colors.gold }} />
                  Secured by enterprise-grade authentication • 256-bit encryption
                </Typography>
              </Box>
            </Box>
          </LuxuryPaper>

          {/* Footer */}
          <Typography 
            variant="caption" 
            align="center" 
            sx={{ 
              display: 'block', 
              mt: 2, 
              color: alpha(colors.cream, 0.7),
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            © 2024 Elite Diagnostics • All rights reserved • Version 2.0
          </Typography>
        </Box>
      </Zoom>
    </LuxuryContainer>
  );
};

// import React, { useState } from 'react';
// import {
//   Container,
//   Paper,
//   Typography,
//   TextField,
//   Button,
//   Box,
//   Alert,
//   Avatar,
//   Divider,
//   Link,
// } from '@mui/material';
// import { 
//   Fingerprint as FingerprintIcon,
//   Person as PersonIcon,
//   VpnKey as VpnKeyIcon 
// } from '@mui/icons-material';
// import { useNavigate, Link as RouterLink } from 'react-router-dom';
// import { useAuth } from '../context/AuthContext';
// import { useMutation } from '@apollo/client/react';
// import { LOGIN_MUTATION } from '../graphql/auth.graphql';
// import { LoginResponse,LoginVariables } from '../types/user.types';

// export const EmployeeLogin: React.FC = () => {
//   const [username, setUsername] = useState('');
//   const [password, setPassword] = useState('');
//   const [error, setError] = useState('');
//   const [loading, setLoading] = useState(false);
  
//   const navigate = useNavigate();
//   const { login } = useAuth();

//   // Properly typed mutation hook
//   const [loginMutation] = useMutation<LoginResponse, LoginVariables>(LOGIN_MUTATION);

//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();
//     setError('');
//     setLoading(true);

//     try {
//       const { data } = await loginMutation({
//         variables: {
//           input: {
//             username,
//             password
//           }
//         }
//       });

//       if (data?.login) {
//         const { token, user } = data.login;
//         login(token, user);
//         navigate('/dashboard');
//       } else {
//         setError('Invalid response from server');
//       }
//     } catch (err: any) {
//       console.error('Login error:', err);
      
//       // Handle different error types
//       if (err.networkError) {
//         setError('Network error. Please check your connection.');
//       } else if (err.graphQLErrors && err.graphQLErrors.length > 0) {
//         setError(err.graphQLErrors[0].message || 'Login failed');
//       } else {
//         setError(err.message || 'Login failed. Please check your credentials.');
//       }
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <Container component="main" maxWidth="xs">
//       <Box
//         sx={{
//           marginTop: 8,
//           display: 'flex',
//           flexDirection: 'column',
//           alignItems: 'center',
//         }}
//       >
//         <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
//           <Box display="flex" flexDirection="column" alignItems="center">
//             <Avatar sx={{ m: 1, bgcolor: 'primary.main', width: 56, height: 56 }}>
//               <FingerprintIcon fontSize="large" />
//             </Avatar>
//             <Typography component="h1" variant="h5" gutterBottom>
//               Employee Login
//             </Typography>
//             <Typography variant="body2" color="textSecondary" gutterBottom>
//               Access Lab Management System
//             </Typography>
//           </Box>

//           {error && (
//             <Alert severity="error" sx={{ mt: 2 }}>
//               {error}
//             </Alert>
//           )}

//           <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
//             <TextField
//               margin="normal"
//               required
//               fullWidth
//               id="username"
//               label="Username"
//               name="username"
//               autoComplete="username"
//               autoFocus
//               value={username}
//               onChange={(e) => setUsername(e.target.value)}
//               InputProps={{
//                 startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />,
//               }}
//             />
//             <TextField
//               margin="normal"
//               required
//               fullWidth
//               name="password"
//               label="Password"
//               type="password"
//               id="password"
//               autoComplete="current-password"
//               value={password}
//               onChange={(e) => setPassword(e.target.value)}
//               InputProps={{
//                 startAdornment: <VpnKeyIcon color="action" sx={{ mr: 1 }} />,
//               }}
//             />
//             <Button
//               type="submit"
//               fullWidth
//               variant="contained"
//               sx={{ mt: 3, mb: 2 }}
//               disabled={loading}
//             >
//               {loading ? 'Logging in...' : 'Login'}
//             </Button>
            
//             <Divider sx={{ my: 2 }} />
            
//             <Box textAlign="center">
//               <Link 
//                 component={RouterLink} 
//                 to="/self-registration" 
//                 variant="body2"
//               >
//                 Are you a patient? Click here to upload prescription
//               </Link>
//             </Box>
//           </Box>
//         </Paper>
//       </Box>
//     </Container>
//   );
// };